<script lang="ts" setup>
import { ref, onBeforeMount, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axiosInstance from '@/plugins/axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const router = useRouter();
const route = useRoute();
const rootLangTexts = ref<Record<string, string>>({});
const targetLangTexts = ref<Record<string, string>>({});
const targetLangCode = ref("undefined");
const targetLangID = ref(route.params.translation);

function stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '');
}

const staticTextLabels = [
  { key: 'disclaimer', label: 'Main Page Disclaimer' },
  { key: 'privacyPolicy', label: 'Data Privacy' },
  { key: 'impressum', label: 'Imprint' },
  { key: 'evaluationResult', label: 'Result Page: Header' },
  { key: 'evaluationSummary', label: 'Result Page: Summary' },
  { key: 'evaluationJustification', label: 'Result Page: Justification' },
  { key: 'evaluationRequirements', label: 'Result Page: Requirements' },
  { key: 'backButton', label: 'Back-Button' },
  { key: 'nextButton', label: 'Next-Button' },
  { key: 'reportDownloadCloseButton', label: 'Report PDF Box: Cancel-Button' },
  { key: 'reportDownloadDescription', label: 'Report PDF Box: Text' },
  { key: 'reportDownloadSaveButton', label: 'Report PDF Box: Download-Button' },
  { key: 'loadingReportText', label: 'Loading Text' },
  { key: 'privacyNoticeText', label: 'Data Privacy: Text' },
  { key: 'privacyNoticeTitle', label: 'Data Privacy: Title' },
  { key: 'legalNoticeText', label: 'Imprint: Text' },
  { key: 'legalNoticeTitle', label: 'Imprint: Title' },
  { key: 'downloadPdfButton', label: 'Result Page: Download-Button' },
  { key: 'restartButton', label: 'Result Page: Restart-Button' },
  { key: 'selectAtLeastOneOption', label: 'Info-Box: Selection for Multiple Choice' },
  { key: 'selectOneOption', label: 'Info-Box: Selection for Single Choice' },
  { key: 'pdfDisclaimer', label: 'Result PDF: PDF-Disclaimer' },
  { key: 'faqTitle', label: 'FAQ: Title' },
];


const getTargetTexts = async () => {
    try {
        const response = await axiosInstance.get('/translations/' + targetLangID.value);
        targetLangTexts.value = response.data.staticTexts;
        targetLangCode.value = response.data.languageCode;
    } catch (error) {
        console.log(error);
    }
};

const getRootTexts = async () => {
    try {
        const rootTranslationId = targetLangCode.value === 'en' ? '670fad078be48456eb53b250' : '670e1e32d4634dd3c3ec7478';
        const response = await axiosInstance.get('/translations/' + rootTranslationId);
        rootLangTexts.value = response.data.staticTexts;
    } catch (error) {
        console.log(error);
    }
};

onBeforeMount(async () => {
    await getTargetTexts();
    await getRootTexts();
});

const rootTextEntries = computed(() => Object.entries(rootLangTexts.value));

function editStaticText(key) {
    router.push({ name: 'StaticTextsDetailView', params: { staticTextKey: key } });
    console.log("Edit", key);
}
</script>

<template>
    <div class="mb-5">
        <DataTable :value="rootTextEntries" tableStyle="min-width: 50rem">

            <!-- Key Column -->
            <Column field="key" header="Key">
                <template #body="{ data }">
                    {{ staticTextLabels.find(item => item.key === data[0])?.label || data[0] }}
                </template>
            </Column>
            
            <!-- Root Language Column with Tooltip -->
            <Column header="Root Language">
                <template #body="{ data }">
                    <span v-tooltip.top="data[1]">
                        {{ stripHtml(data[1]).length > 30 ? stripHtml(data[1]).substring(0, 30) + '...' : stripHtml(data[1]) }}
                    </span>
                </template>
            </Column>
            
            <!-- Target Language Column with Tooltip -->
            <Column header="Target Language">
                <template #body="{ data }">
                    <span v-tooltip.top="stripHtml(targetLangTexts[data[0]] || '')">
                        {{ targetLangTexts[data[0]] && stripHtml(targetLangTexts[data[0]]).length > 30
                            ? stripHtml(targetLangTexts[data[0]]).substring(0, 30) + '...'
                            : stripHtml(targetLangTexts[data[0]] || '') }}
                    </span>
                    <Tag v-if="!targetLangTexts[data[0]]" severity="warn">Not translated</Tag>
                </template>
            </Column>
            
            <!-- Edit Button Column -->
            <Column header="Actions">
                <template #body="{ data }">
                    <Button @click="editStaticText(data[0])" class="p-button-tuev">
                        <template #icon>
                            <FontAwesomeIcon icon="edit" />
                        </template>
                    </Button>
                </template>
            </Column>
        </DataTable>
    </div>
</template>


<style scoped>
.tooltip {
    cursor: pointer;
    border-bottom: 1px dotted;
    text-decoration: none;
}
</style>
